import React, { useState, useEffect, useRef } from 'react';
import { searchPlayersDB } from '../../lib/db';

export default function SearchBox({ onReady, puzzle, activeCell, usedPlayers, onSubmit, suggestionsOff }) {
  const [query, setQuery]           = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading]       = useState(false);

  const { r, c } = activeCell;
  const rowLabel = puzzle.row_labels[r]?.label;
  const colLabel = puzzle.col_labels[c]?.label;

  // A player is exempt from the "already used" indicator if they are the
  // sole valid answer for the current cell
  const currentCellAnswers = puzzle.puzzle_cells?.find(
    pc => pc.row_index === r && pc.col_index === c
  )?.valid_answers || [];
  const isSoleAnswerForCell = (playerName) =>
    currentCellAnswers.length === 1 &&
    currentCellAnswers[0].toLowerCase() === playerName.toLowerCase();

  const [_hp,    setHp]    = useState(''); // honeypot — bots fill this, humans don't
  const inputRef = useRef(null);

  // Expose focus function to parent so cell tap can trigger it directly
  // (programmatic focus from useEffect is blocked on iOS Safari)
  useEffect(() => {
    if (onReady) onReady(() => inputRef.current?.focus());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scroll search box into view when active cell changes (mobile UX)
  const searchBoxRef = useRef(null);

  // Reset query and explicitly focus input whenever active cell changes.
  // autoFocus alone only works on first mount — Chrome desktop won't
  // re-focus on re-render, so we call .focus() imperatively here.
  useEffect(() => {
    setQuery('');
    setSuggestions([]);
    // Scroll search box into view on mobile — keyboard opens on user tap
    const t = setTimeout(() => {
      searchBoxRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
    return () => clearTimeout(t);
  }, [r, c]);

  // Search as user types — skip if suggestions are off
  useEffect(() => {
    if (suggestionsOff) { setSuggestions([]); return; }
    if (query.trim().length < 2) { setSuggestions([]); return; }
    const timeout = setTimeout(async () => {
      setLoading(true);
      const q = query.trim().toLowerCase();

      // 1. Search the global players DB
      const dbResults = await searchPlayersDB(query);
      const dbNames = new Set(dbResults.map(p => p.name.toLowerCase()));

      // 2. Also search across ALL valid_answers in this puzzle
      //    so suggestions work even if the player isn't in the DB yet
      const puzzleNames = [];
      if (puzzle?.puzzle_cells) {
        for (const cell of puzzle.puzzle_cells) {
          for (const name of (cell.valid_answers || [])) {
            if (name.toLowerCase().includes(q) && !dbNames.has(name.toLowerCase())) {
              puzzleNames.push({ id: name, name, nationality: 'puzzle' });
            }
          }
        }
      }

      // Merge: DB results first, then puzzle-only names (deduped)
      setSuggestions([...dbResults, ...puzzleNames]);
      setLoading(false);
    }, 200);
    return () => clearTimeout(timeout);
  }, [query, suggestionsOff, puzzle]);

  const handleSubmit = () => {
    if (_hp) return; // bot detected — silently ignore
    if (query.trim().length < 2) return;
    onSubmit(query.trim());
    setQuery('');
    setSuggestions([]);
  };

  const handleSuggestionClick = (name) => {
    const used = usedPlayers.has(name.toLowerCase());
    if (used) return;
    setQuery(name);
    setSuggestions([]);
  };

  return (
    <div ref={searchBoxRef} className="mt-2.5 bg-surface2 border border-border rounded-xl overflow-hidden transition-all focus-within:border-accent animate-fade-up">
      {/* Label */}
      <div className="px-3.5 pt-2.5 pb-1 font-mono text-[11px] text-dimText tracking-wide">
        <span className="text-accent font-semibold">{colLabel}</span>
        &nbsp;+&nbsp;
        <span className="text-accent font-semibold">{rowLabel}</span>
      </div>

      {/* Input row */}
      <div className="flex items-center">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSubmit()}
          placeholder={suggestionsOff ? 'Type exact player name…' : 'Type a player name…'}
          autoComplete="off"
          autoCorrect="off"
          spellCheck="false"
          className="flex-1 bg-transparent border-none outline-none text-white text-base font-medium px-3.5 pb-3 pt-1 placeholder:text-[var(--text-dimmer)]"
        />
        <button
          onClick={handleSubmit}
          disabled={query.trim().length < 2}
          className="bg-accent text-black font-bold text-sm px-4 py-2 m-1.5 rounded-lg tracking-wide transition-colors hover:bg-[#33eeff] disabled:bg-surface3 disabled:text-dimText disabled:cursor-default"
        >
          Enter
        </button>
      </div>

      {/* Honeypot — hidden from real users, bots fill it */}
      <input
        type="text"
        value={_hp}
        onChange={e => setHp(e.target.value)}
        tabIndex={-1}
        aria-hidden="true"
        style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0 }}
      />

      {/* Suggestions */}
      {(suggestions.length > 0 || loading) && (
        <div className="border-t border-border max-h-44 overflow-y-auto">
          {loading && (
            <div className="px-3.5 py-2.5 text-sm text-dimText font-mono">Searching…</div>
          )}
          {suggestions.map(player => {
            const used = usedPlayers.has(player.name.toLowerCase()) && !isSoleAnswerForCell(player.name);
            return (
              <div
                key={player.id}
                onClick={() => handleSuggestionClick(player.name)}
                className={`flex justify-between items-center px-3.5 py-2.5 text-sm border-b border-border last:border-0 transition-colors
                  ${used ? 'opacity-35 cursor-not-allowed' : 'cursor-pointer hover:bg-surface3'}`}
              >
                <span>{player.name}</span>
                <span className={`font-mono text-[11px] ${used ? 'text-wrong' : 'text-dimText'}`}>
                  {used ? 'already used' : player.nationality}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
